import PropTypes from "prop-types";
import { useRef, useContext, useState } from "react";
import styles from "../../../ProductTile.css";
import getDealDetails from "./getDealDetails";
import ProductDescription from "../../../ProductDescription";
import ProductPrice from "./ProductPrice";
import SaveForLater from "../../../SaveForLater";
import Banners from "../../../Banners";
import ProductLink from "../../../ProductLink";
import classnames from "classnames";
import { withTranslation } from "../../../../translation";
import { MIX_MATCH_GROUP } from "../../../../../state/modules/search/constants";
import getAriaLabelledBy from "../../../getAriaLabelledBy";
import useOnViewBeacon from "../../../UseOnViewBeacon";
import useOnLoadBeacon from "../../../useOnLoadBeacon";
import TileContext from "../../../../TileContext";
import { TILE_SIZES } from "../../../../../helpers/constants";
import ProductMediaContainer from "../../../ProductMediaContainer";

const { TILE_SIZE_TWO_BY_ONE, TILE_SIZE_TWO_BY_TWO } = TILE_SIZES;

const getAriaLabel = ({
  description,
  isSale,
  fromLabel,
  formattedPrice,
  formattedReducedPrice,
  formattedDiscountedPrice,
  formattedBenefitValue,
  discountCode,
  deal,
  formatTranslation,
}) => {
  const isPromotionalDiscount = formattedBenefitValue != null;

  if (isPromotionalDiscount) {
    const originalPrice = formattedReducedPrice
      ? formattedReducedPrice
      : formattedPrice;
    const codeLabel = discountCode ? `: ${discountCode}` : "";

    return `${description}, ${formatTranslation(
      "plp_web_a11y_original_price",
    )} ${fromLabel}${originalPrice} ${formatTranslation(
      "generic_current_price",
    )} ${formattedDiscountedPrice} ${formattedBenefitValue} ${formatTranslation(
      "generic_with_code",
    )}${codeLabel}`;
  }

  if (isSale && formattedReducedPrice) {
    const dealPart = deal
      ? `, ${formatTranslation("generic_accessibility_discount")}: ${deal}`
      : "";

    return `${description}, ${formatTranslation(
      "plp_web_a11y_original_price",
    )} ${formattedPrice} ${formatTranslation(
      "generic_current_price",
    )} ${fromLabel} ${formattedReducedPrice}${dealPart}`;
  }

  return `${description}, ${formatTranslation(
    "plp_web_a11y_price",
  )} ${formattedPrice}`;
};

export const ProductTile = ({
  id,
  tileId,
  url,
  price,
  isSale,
  isOutlet,
  productType,
  description,
  colourWayId,
  reducedPrice,
  formatCurrency,
  formatTranslation,
  hasMultiplePrices,
  ProductHeroComponent,
  analyticsProductClick,
  className,
  carouselCatId,
  euOmnibusEnabled,
  dealBannerEnabled,
  discounted,
  advertisement: {
    id: advertId,
    onClickBeacon,
    onViewBeacon,
    onLoadBeacon,
    onWishlistBeacon,
  },
  sendPlacementOnViewBeacon,
  uponTileBadges = () => null,
  underneathTileBadges = () => null,
  ...rest
}) => {
  const [focused, setFocused] = useState(false);
  const getProductTileId = (id) => `product-${id}`;
  const productTileId = getProductTileId(id);
  const labels = getAriaLabelledBy({ productId: productTileId });
  const { tileSize } = useContext(TileContext);
  const fromLabel = hasMultiplePrices
    ? `${formatTranslation("plp_web_product_tile_price_from")} `
    : "";
  const tileElement = useRef();

  useOnLoadBeacon({ onLoadBeacon });

  useOnViewBeacon({
    targetElement: tileElement.current,
    onViewBeacon,
    sendPlacementOnViewBeacon,
  });

  const wrappedAnalyticsProductClick = (obj) =>
    analyticsProductClick({ ...obj, product: { ...rest } });

  const {
    formattedPrice,
    formattedReducedPrice,
    deal,
    dealAria,
    formattedBenefitValue,
    discountCode,
    formattedDiscountedPrice,
  } = getDealDetails({
    price,
    reducedPrice,
    formatCurrency,
    formatTranslation,
    euOmnibusEnabled,
    dealBannerEnabled,
    discounted,
  });

  const ariaLabelFormatted = getAriaLabel({
    description,
    isSale,
    fromLabel,
    formattedPrice,
    formattedReducedPrice,
    formattedDiscountedPrice,
    formattedBenefitValue,
    discountCode,
    deal,
    formatTranslation,
  });

  return (
    <li
      ref={tileElement}
      id={getProductTileId(tileId)}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      className={classnames(
        styles.productTile,
        {
          [styles.twoByOne]: tileSize === TILE_SIZE_TWO_BY_ONE,
          [styles.twoByTwo]: tileSize === TILE_SIZE_TWO_BY_TWO,
        },
        className,
      )}
    >
      <ProductLink
        ariaLabel={ariaLabelFormatted}
        className={styles.productLink}
        href={url}
        productTileId={productTileId}
        carouselCatId={carouselCatId}
        productId={id}
        onClickBeacon={onClickBeacon}
        advertId={advertId}
        analyticsProductClick={wrappedAnalyticsProductClick}
        {...rest}
      >
        <ProductMediaContainer className={styles.mediaContainer}>
          <ProductHeroComponent
            description={description}
            focused={focused}
            {...rest}
          />
          <Banners {...{ labels, deal, dealAria, ...rest }} />
          {uponTileBadges({ labels, ...rest })}
        </ProductMediaContainer>
        <div
          className={styles.productInfo}
          aria-label={ariaLabelFormatted}
          // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
          tabIndex={0}
          role="region"
        >
          <ProductDescription text={description} labelledById={labels.title} />
          <ProductPrice
            price={formattedPrice}
            isSale={isSale}
            isOutlet={isOutlet}
            reducedPrice={formattedReducedPrice}
            hasMultiplePrices={hasMultiplePrices}
            labelledById={labels.price}
            euOmnibusEnabled={euOmnibusEnabled}
            discountBenefitValue={formattedBenefitValue}
            discountCode={discountCode}
            discountedPrice={formattedDiscountedPrice}
            deal={deal}
            dealAria={dealAria || ""}
            {...rest}
          />
          {underneathTileBadges({ labels, ...rest })}
        </div>
      </ProductLink>
      {productType !== MIX_MATCH_GROUP && (
        <SaveForLater
          {...{
            productId: id,
            price,
            reducedPrice,
            colourWayId,
            carouselCatId,
            description,
            onWishlistBeacon,
            className: styles.bottomRightButton,
            ...rest,
          }}
        />
      )}
    </li>
  );
};

ProductTile.propTypes = {
  isSale: PropTypes.bool,
  isOutlet: PropTypes.bool,
  reducedPrice: PropTypes.number,
  id: PropTypes.number.isRequired,
  tileId: PropTypes.string.isRequired,
  url: PropTypes.string.isRequired,
  hasMultiplePrices: PropTypes.bool,
  price: PropTypes.number.isRequired,
  description: PropTypes.string.isRequired,
  colourWayId: PropTypes.number.isRequired,
  formatCurrency: PropTypes.func.isRequired,
  formatTranslation: PropTypes.func.isRequired,
  productType: PropTypes.string.isRequired,
  ProductHeroComponent: PropTypes.elementType.isRequired,
  className: PropTypes.string,
  carouselCatId: PropTypes.number,
  euOmnibusEnabled: PropTypes.bool.isRequired,
  dealBannerEnabled: PropTypes.bool.isRequired,
  discounted: PropTypes.shape({
    value: PropTypes.number,
    text: PropTypes.string,
    discountCode: PropTypes.string,
    discountBenefitValue: PropTypes.number,
  }),
  advertisement: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    onClickBeacon: PropTypes.string.isRequired,
    onViewBeacon: PropTypes.string.isRequired,
    onLoadBeacon: PropTypes.string.isRequired,
    onWishlistBeacon: PropTypes.string.isRequired,
  }),
  sendPlacementOnViewBeacon: PropTypes.func,
  analyticsProductClick: PropTypes.func,
  uponTileBadges: PropTypes.func,
  underneathTileBadges: PropTypes.func,
};

export default withTranslation(ProductTile);
